import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, RefreshCw } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (err) {
        console.error("Settings error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/settings", settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full p-8">
      <RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />
    </div>
  );

  const update = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 max-w-2xl" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Settings</h1>
        <Button
          data-testid="save-settings-btn"
          onClick={handleSave}
          disabled={saving}
          className="h-8 bg-white text-black hover:bg-gray-200 rounded-sm text-xs"
        >
          <Save className="w-3 h-3 mr-1" />
          {saving ? "Saving..." : saved ? "Saved" : "Save Settings"}
        </Button>
      </div>

      <Tabs defaultValue="account" className="w-full">
        <TabsList className="bg-[#111111] border border-[#27272A] rounded-sm h-8 p-0.5">
          <TabsTrigger value="account" className="text-xs h-7 rounded-sm px-3" data-testid="settings-tab-account">Account</TabsTrigger>
          <TabsTrigger value="risk" className="text-xs h-7 rounded-sm px-3" data-testid="settings-tab-risk">Risk Limits</TabsTrigger>
          <TabsTrigger value="rules" className="text-xs h-7 rounded-sm px-3" data-testid="settings-tab-rules">Strategy Rules</TabsTrigger>
        </TabsList>

      <TabsContent value="account" className="space-y-4 md:space-y-6 mt-4">
      {/* Account */}
      <section className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <h2 className="text-sm font-bold mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Account Configuration</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Account Type</Label>
            <Select value={settings?.account_type || "reg_t"} onValueChange={(v) => update("account_type", v)}>
              <SelectTrigger className="h-8 w-full bg-[#0A0A0A] border-[#27272A] text-xs rounded-sm" data-testid="settings-account-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111111] border-[#27272A]">
                <SelectItem value="reg_t" className="text-xs text-[#F4F4F5]">Reg T Margin</SelectItem>
                <SelectItem value="portfolio_margin" className="text-xs text-[#F4F4F5]">Portfolio Margin</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[9px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
              Determines which margin model is used in margin and buying-power calculations. Select the type that matches your brokerage account.
            </p>
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Net Liquidation Value ($)</Label>
            <Input
              data-testid="settings-nlv"
              type="number"
              value={settings?.nlv || ""}
              onChange={(e) => update("nlv", parseFloat(e.target.value))}
              className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
            />
            <p className="text-[9px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
              Your account's total value if all positions were closed at current prices. Used as the denominator for utilization and risk calculations.
            </p>
          </div>
        </div>
      </section>

      <section className="border rounded-sm p-3 md:p-4" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
        data-testid="settings-disclaimer">
        <h2 className="text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: "var(--hoip-text-tertiary)" }}>Legal & Disclaimers</h2>
        <p className="text-[11px] leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>
          MarkovLab
          provides analytics and decision-support tools for self-directed investors. It does not provide investment,
          legal, tax, or trading advice and is not a broker-dealer, investment adviser, or portfolio manager. Any
          tickers or symbols shown on the platform are used for illustrative purposes only and do not constitute a
          recommendation to buy, sell, or hold any security. Users are responsible for their own investment decisions
          and for any orders placed with their brokers.
        </p>
        <p className="mt-2 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          All metrics labelled "Favourable", "Caution", or "Unfavourable" describe model-derived classifications, not directives to act. Risk labels ("Low", "Medium", "High") describe regime conditions and probability bands, not trade recommendations.
        </p>
      </section>
      </TabsContent>

      <TabsContent value="risk" className="space-y-4 md:space-y-6 mt-4">
      {/* Risk Limits */}
      <section className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <h2 className="text-sm font-bold mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Risk Limits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { field: "max_margin_utilization", label: "Max Margin Utilization (%)", placeholder: "40", hint: "The maximum share of your buying power the platform will treat as deployable. The 40% default is a commonly referenced heuristic for keeping a buffer against margin expansion in drawdowns. General industry reference, not a personalized recommendation." },
            { field: "max_risk_per_trade", label: "Max Risk Per Trade (%)", placeholder: "3", hint: "Caps the capital at risk in any single position as a percentage of net liquidation value. The 3% default sits within the commonly referenced 1–5% range for position sizing. General industry reference, not a personalized recommendation." },
            { field: "max_sector_concentration", label: "Max Sector Concentration (%)", placeholder: "25", hint: "Cap on basket premium from any single sector — two tickers in one sector still move together on a common shock." },
            { field: "max_single_ticker_pct", label: "Max Single Ticker (%)", placeholder: "10" },
            { field: "max_shorts_same_week", label: "Max Shorts Same Week", placeholder: "5", type: "int" },
            { field: "target_credit_min_pct", label: "Target Credit Min (%)", placeholder: "15" },
            { field: "max_contracts_per_ticker", label: "Max Contracts per Ticker (basket)", placeholder: "2", type: "int", hint: "Hard cap on stacking one name in a basket — 4 contracts of the same ticker are 4 correlated bad trades if it gaps down." },
            { field: "max_ticker_basket_pct", label: "Max % of Basket per Ticker", placeholder: "30", hint: "Caps any single ticker's share of the basket premium target. Enforced together with the contract cap — stricter wins." },
            { field: "regime_disagree_threshold_pct", label: "Regime Disagreement Alert Threshold (%)", placeholder: "5", hint: "Flags tickers in the Scanner where the 5-day smoothed regime and the instantaneous regime disagreed over more than this percentage of the last 60 bars. Lower thresholds surface more potential regime instability; higher thresholds only flag stronger disagreements. A display control for the disagreement flag — not a personalized recommendation." },
            { field: "profit_protect_gain_pct", label: "Profit-Protection: Unrealized Gain Trigger (%)", placeholder: "15", hint: "Position Management flags a profit-protection context when a stock position's unrealized gain crosses this threshold together with the 1-day move trigger below. General industry convention as a configurable starting point — informational only, not personalized advice." },
            { field: "profit_protect_day_move_pct", label: "Profit-Protection: 1-Day Move Trigger (%)", placeholder: "5", hint: "Second condition for the profit-protection flag: the favorable move happened fast (same-day change at or above this percentage). General industry convention as a configurable starting point — informational only, not personalized advice." },
            { field: "profit_protect_ivr_max", label: "Profit-Protection: IV-Rank Ceiling", placeholder: "30", hint: "When both triggers fire and IV Rank is BELOW this level, the flag notes that protection (puts) is conventionally inexpensive; at or above it, the flag notes that elevated premium conventionally favors covered calls instead — shown only when the fundamentals composite meets your hold threshold. General industry convention — informational only, not personalized advice." },
            { field: "assign_fund_min", label: "Assignment Context: Fundamentals Floor (0-100)", placeholder: "60", hint: "For short puts / CSPs: when the underlying's fundamentals composite is at or above this score AND IV Rank meets the threshold below, Position Management shows an 'assignment-friendly' context per common wheel-strategy convention. Also used as the hold gate for the covered-call profit-protection note. Informational only, not personalized advice." },
            { field: "assign_ivr_min", label: "Assignment Context: IV-Rank Floor", placeholder: "50", hint: "Second condition for the assignment-friendly context — elevated IV Rank conventionally favors selling calls after assignment. General industry convention as a configurable starting point — informational only, not personalized advice." },
            { field: "roll_fund_max", label: "Roll Context: Fundamentals Ceiling (0-100)", placeholder: "40", hint: "When the underlying's fundamentals composite falls below this score, Position Management shows a 'roll context' note — general options-management practice favors examining roll/close paths over assignment when fundamentals weaken. Informational only, not personalized advice." },
          ].map(({ field, label, placeholder, type, hint }) => (
            <div key={field}>
              <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>{label}</Label>
              <Input
                data-testid={`settings-${field}`}
                type="number"
                step={type === "int" ? "1" : "0.1"}
                value={settings?.[field] ?? ""}
                onChange={(e) => update(field, type === "int" ? parseInt(e.target.value) : parseFloat(e.target.value))}
                placeholder={placeholder}
                className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
              />
              {hint && (
                <p className="text-[9px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>{hint}</p>
              )}
            </div>
          ))}
        </div>
        {/* Iter182 — Basket Screener earnings-DTE filter */}
        <div className="mt-4 pt-3 border-t space-y-2" style={{ borderColor: "var(--hoip-divider)" }} data-testid="settings-earnings-filter">
          <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--hoip-text-secondary)" }}>
            <input
              type="checkbox"
              data-testid="settings-avoid-earnings-dte"
              checked={settings?.basket_avoid_earnings_dte ?? true}
              onChange={(e) => update("basket_avoid_earnings_dte", e.target.checked)}
              className="accent-emerald-500"
            />
            Avoid DTEs spanning earnings (Basket Screener)
          </label>
          <p className="text-[9px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
            When on, the basket screener excludes any strike/DTE combination that would still be open through the
            ticker's next earnings date — an earnings gap can move through short strikes in a single session.
          </p>
          <div className="max-w-xs">
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Earnings date strictness</Label>
            <Select value={settings?.earnings_filter_mode || "confirmed_estimated"} onValueChange={(v) => update("earnings_filter_mode", v)}>
              <SelectTrigger className="h-8 w-full bg-[#0A0A0A] border-[#27272A] text-xs rounded-sm" data-testid="settings-earnings-filter-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111111] border-[#27272A]">
                <SelectItem value="confirmed_estimated" className="text-xs text-[#F4F4F5]">Confirmed + estimated dates (safer)</SelectItem>
                <SelectItem value="confirmed_only" className="text-xs text-[#F4F4F5]">Confirmed dates only (more candidates)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Iter183a — §3 Portfolio Risk Limits. Consumed by
          services/portfolio_risk_health.py (nav LED, Wheel strip, basket gate). */}
      <section className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="settings-portfolio-risk-limits">
        <h2 className="text-sm font-bold mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>Portfolio Risk Limits</h2>
        <p className="text-[10px] mb-3" style={{ color: "var(--hoip-text-tertiary)" }}>
          Drives the Portfolio Risk Health indicator (nav LED, Wheel Income strip, basket screen warnings).
          All thresholds are yours to set. Defaults reflect commonly referenced practice for income-oriented
          portfolios — general industry reference, not a personalized recommendation.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { field: "max_single_position_pct", label: "Max Single Position (% NetLiq)", placeholder: "10", hint: "Standard single-position cap for income portfolios — prevents one name from dominating outcomes." },
            { field: "max_wheel_allocation_pct", label: "Max Wheel/Income Allocation (%)", placeholder: "25", hint: "Keeps premium-selling one sleeve of the portfolio (measured as options margin vs NetLiq), not the whole book." },
            { field: "max_sector_exposure_pct", label: "Max Sector Exposure (% NetLiq)", placeholder: "15", hint: "Two different tickers in the same sector can still move together on a common shock." },
            { field: "bwd_min", label: "Beta-Weighted Delta Min (× NLV)", placeholder: "0.3", hint: "Lower band — below this the book may be under-invested vs its income mandate." },
            { field: "bwd_max", label: "Beta-Weighted Delta Max (× NLV)", placeholder: "0.7", hint: "Upper band — above this the income book has silently become a large directional bet." },
            { field: "max_bp_utilization_pct", label: "Max Margin Utilization (% NetLiq)", placeholder: "50", hint: "Leaves a real cushion before maintenance-margin calls become a risk." },
            { field: "min_cash_buffer_pct", label: "Min Liquidity Buffer (% NetLiq)", placeholder: "25", hint: "Core anti-blow-up rule — reserve capital must exist before stress hits, not be discovered after." },
            { field: "stress_scenario_pct", label: "Stress Scenario (% drawdown)", placeholder: "10", hint: "Adverse-move scenario used for the liquidity-floor stress test (snaps to nearest 5%)." },
            { field: "stress_liq_floor_pct", label: "Stressed Liquidity Floor (% NetLiq)", placeholder: "10", hint: "Flag red if the stress scenario pushes projected excess liquidity below this floor." },
          ].map(({ field, label, placeholder, hint }) => (
            <div key={field}>
              <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>{label}</Label>
              <Input
                data-testid={`settings-${field}`}
                type="number"
                step="0.1"
                value={settings?.[field] ?? ""}
                onChange={(e) => update(field, parseFloat(e.target.value))}
                placeholder={placeholder}
                className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
              />
              {hint && (
                <p className="text-[9px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>{hint}</p>
              )}
            </div>
          ))}
        </div>
        <p className="text-[9px] mt-3" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="risk-limits-gamma-note">
          Net portfolio gamma is not yet tracked (planned as a batched end-of-day snapshot). Beta-weighted delta uses
          per-ticker betas with a β=1.0 fallback, badged "approx" when a real beta is unavailable.
        </p>
      </section>
      </TabsContent>

      <TabsContent value="rules" className="space-y-4 md:space-y-6 mt-4">
      {/* Iter178 — Position Management Rules panel. Editable + persisted
          to user_settings. Values are read by services/roll_close_advisor
          via routes/scanner.py::_load_open_legs -> enrich_legs on every
          Wheel-Strategies load, so any change here takes effect on the
          next refresh. */}
      <section id="position-mgmt" className="border p-4 scroll-mt-20"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="settings-position-mgmt">
        <h2 className="text-sm font-bold mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>Income Strategy Rules (CSP / BPS / JDL / CC / Synthetic CC)</h2>
        <p className="text-[10px] mb-3" style={{ color: "var(--hoip-text-tertiary)" }}>
          Defaults reflect industry-standard gamma-risk management practice for premium selling. Adjust to your own rules at any time.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>DTE threshold (days)</Label>
            <Input
              data-testid="settings-dte-threshold"
              type="number"
              step="1"
              min="0"
              value={settings?.dte_threshold ?? 21}
              onChange={(e) => update("dte_threshold", parseInt(e.target.value))}
              className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
            />
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Tier 1 loss (% of credit)</Label>
            <Input
              data-testid="settings-loss-tier1"
              type="number"
              step="1"
              min="0"
              value={settings?.loss_tier1_pct ?? 100}
              onChange={(e) => update("loss_tier1_pct", parseFloat(e.target.value))}
              className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
            />
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Tier 2 loss (% of credit)</Label>
            <Input
              data-testid="settings-loss-tier2"
              type="number"
              step="1"
              min="0"
              value={settings?.loss_tier2_pct ?? 200}
              onChange={(e) => update("loss_tier2_pct", parseFloat(e.target.value))}
              className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
            />
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Profit target (% of credit)</Label>
            <Input
              data-testid="settings-tp-target"
              type="number"
              step="1"
              min="0"
              value={settings?.profit_target_pct ?? 50}
              onChange={(e) => update("profit_target_pct", parseFloat(e.target.value))}
              className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
            />
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Max roll debit (% of credit)</Label>
            <Input
              data-testid="settings-roll-max-debit"
              type="number"
              step="1"
              min="0"
              value={settings?.roll_max_debit_pct ?? 25}
              onChange={(e) => update("roll_max_debit_pct", parseFloat(e.target.value))}
              className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
            />
            <p className="text-[9px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
              Caps how much of the original credit can be given back to roll. Hierarchy: duration first, strike second.
            </p>
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Delta early-warning (Δ)</Label>
            <Input
              data-testid="settings-delta-warn"
              type="number"
              step="1"
              min="0"
              value={settings?.delta_warn_threshold ?? 32}
              onChange={(e) => update("delta_warn_threshold", parseFloat(e.target.value))}
              className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
            />
            <p className="text-[9px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
              Short-strike delta that flags a position testing the strike early (30–35Δ ≈ POT 60–70%), before the DTE window.
            </p>
          </div>
        </div>
        <p className="text-[10px] mt-3 italic leading-relaxed" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="position-mgmt-disclaimer">
          These thresholds are commonly referenced heuristics in options-management communities, provided as configurable defaults.
          MarkovLab does not endorse any specific threshold as optimal or suitable for your circumstances. You are responsible for
          setting and acting on your own management rules.
        </p>
      </section>

      {/* Iter183b §4A/§4C — Stock/Equity Rules (consumed by
          services/position_verdicts via /position-management/overview). */}
      <section className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="settings-stock-rules">
        <h2 className="text-sm font-bold mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>Stock / Equity Rules</h2>
        <p className="text-[10px] mb-3" style={{ color: "var(--hoip-text-tertiary)" }}>
          Drives the Hold / Trim / Hedge / Exit verdicts on the Position Management tab.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { field: "profit_lock_gain_pct", label: "Profit-Lock Gain Trigger (%)", placeholder: "100", hint: "Once a position has at least doubled, giving back a chunk from the peak signals locking gains rather than round-tripping a big winner." },
            { field: "profit_lock_drawdown_pct", label: "Profit-Lock Drawdown from High (%)", placeholder: "15", hint: "The give-back from the 1-yr high (combined with the gain trigger) that fires the profit-lock rule." },
            { field: "trailing_sma_period", label: "Trailing Stop SMA (days)", placeholder: "50", type: "int", hint: "Trend-following exit signal — a close below this moving average avoids reacting to single-day noise." },
          ].map(({ field, label, placeholder, type, hint }) => (
            <div key={field}>
              <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>{label}</Label>
              <Input
                data-testid={`settings-${field}`}
                type="number"
                step={type === "int" ? "1" : "0.1"}
                value={settings?.[field] ?? ""}
                onChange={(e) => update(field, type === "int" ? parseInt(e.target.value) : parseFloat(e.target.value))}
                placeholder={placeholder}
                className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
              />
              <p className="text-[9px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>{hint}</p>
            </div>
          ))}
          <div className="space-y-2 pt-1">
            <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--hoip-text-secondary)" }}>
              <input type="checkbox" data-testid="settings-fundamental-flag"
                checked={settings?.fundamental_flag_enabled ?? true}
                onChange={(e) => update("fundamental_flag_enabled", e.target.checked)}
                className="accent-emerald-500" />
              Fundamental deterioration flag
            </label>
            <p className="text-[9px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
              Flags positions whose revenue growth turns negative YoY — the thesis may be broken, not just the price. (Margin-compression check planned.)
            </p>
            <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--hoip-text-secondary)" }}>
              <input type="checkbox" data-testid="settings-regime-flag"
                checked={settings?.regime_flag_enabled ?? true}
                onChange={(e) => update("regime_flag_enabled", e.target.checked)}
                className="accent-emerald-500" />
              Regime flag
            </label>
            <p className="text-[9px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
              A regime flip to Bear/Volatile downgrades the verdict even before price breaks down.
            </p>
          </div>
        </div>
      </section>

      {/* Iter183b §4A — Hedge & Protection Rules ("Hedge instead of stop"). */}
      <section className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="settings-hedge-rules">
        <h2 className="text-sm font-bold mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>Hedge & Protection Rules</h2>
        <p className="text-[10px] mb-3" style={{ color: "var(--hoip-text-tertiary)" }}>
          "Hedge instead of stop": when a winner's put IV is cheap vs its own history, a protective put can lock a floor
          without the whipsaw risk of a hard stop. Estimated put cost is shown alongside for comparison.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { field: "hedge_min_gain_pct", label: "Min Gain to Consider (%)", placeholder: "40", hint: "Only positions up at least this much trigger the hedge flag." },
            { field: "hedge_ivr_threshold", label: "IV Rank Below (%)", placeholder: "30", hint: "Puts are 'cheap' when IV Rank is under this vs the name's own 1-yr IV history." },
            { field: "hedge_put_otm_pct", label: "Protective Put OTM (%)", placeholder: "7.5", hint: "Strike distance below spot for the cost estimate (5–10% typical)." },
            { field: "hedge_put_dte", label: "Protective Put DTE (days)", placeholder: "38", type: "int", hint: "Target expiration for the cost estimate (30–45d typical)." },
          ].map(({ field, label, placeholder, type, hint }) => (
            <div key={field}>
              <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>{label}</Label>
              <Input
                data-testid={`settings-${field}`}
                type="number"
                step={type === "int" ? "1" : "0.1"}
                value={settings?.[field] ?? ""}
                onChange={(e) => update(field, type === "int" ? parseInt(e.target.value) : parseFloat(e.target.value))}
                placeholder={placeholder}
                className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data"
              />
              <p className="text-[9px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>{hint}</p>
            </div>
          ))}
        </div>
      </section>
      </TabsContent>
      </Tabs>
    </div>
  );
}
